import React, { memo } from "react";
import type { DocumentGroup } from "services/document-group-api";

import { useMetadocOptions } from "../../hooks/page-operations/useMetadocOptions";
import { getDocumentTypeLabel } from "../../utils/documentTypeUtils";
import PdfPageRenderer from "../pdf-viewer/PdfPageRenderer";
import { getThumbnailItemClassName } from "./utils/thumbnailUtils";
import { THUMBNAIL_CONFIG, ARIA_LABELS } from "./constants/thumbnail.constants";

import type { PDFDocumentProxy } from "pdfjs-dist";
import type { PageState } from "../../metadoc.types";

// use fixed thumbnail height to prevent scrollbar size changes
export const FIXED_THUMBNAIL_HEIGHT = THUMBNAIL_CONFIG.FIXED_HEIGHT;

type MetaDocThumbnailItemProps = {
  pdfDoc: PDFDocumentProxy;
  pageNum: number;
  isActive: boolean;
  isSelected: boolean;
  isFocused: boolean;
  documentGroup: DocumentGroup | null;
  pageState?: PageState;
  virtualRowStart: number;
  onClick: (pageNum: number) => void;
  onFocus: (pageNum: number) => void;
  onBlur: () => void;
  onCheckboxChange: (
    pageNum: number,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  disabled?: boolean;
  selectionLocked?: boolean;
};

const MetaDocThumbnailItem = memo(
  ({
    pdfDoc,
    pageNum,
    isActive,
    isSelected,
    isFocused,
    documentGroup,
    pageState,
    virtualRowStart,
    onClick,
    onFocus,
    onBlur,
    onCheckboxChange,
    disabled = false,
    selectionLocked = false,
  }: MetaDocThumbnailItemProps) => {
    const { getDocumentTypeColor } = useMetadocOptions();
    // check if page is being deleted
    // this applies CSS classes (--deleted) for visual styling and disables interactions
    const isDeleted = pageState === "deleting";
    const isInteractionDisabled = disabled || isDeleted;

    return (
      <div
        data-index={pageNum - 1}
        className={getThumbnailItemClassName({
          isActive,
          isSelected,
          isFocused,
          isDeleted,
          disabled,
        })}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: `${FIXED_THUMBNAIL_HEIGHT}px`,
          transform: `translateY(${virtualRowStart}px)`,
        }}
        onClick={isInteractionDisabled ? undefined : () => onClick(pageNum)}
        onFocus={isInteractionDisabled ? undefined : () => onFocus(pageNum)}
        onBlur={isInteractionDisabled ? undefined : onBlur}
        tabIndex={isInteractionDisabled ? -1 : 0}
        role={isInteractionDisabled ? undefined : "button"}
        aria-label={ARIA_LABELS.pageStatus(pageNum, isDeleted, disabled)}
      >
        <div className="metadoc-page-thumbnails__item-content">
          <div className="metadoc-page-thumbnails__thumbnail">
            <PdfPageRenderer
              pdfDoc={pdfDoc}
              pageNum={pageNum}
              scale={THUMBNAIL_CONFIG.SCALE}
            />
            <div className="metadoc-page-thumbnails__page-number">
              {pageNum}
            </div>
            {documentGroup && (
              <div
                className="metadoc-page-thumbnails__badge"
                style={{
                  backgroundColor: getDocumentTypeColor(
                    documentGroup.document_type,
                  ),
                }}
                title={documentGroup.document_type}
              >
                {getDocumentTypeLabel(documentGroup.document_type)}
              </div>
            )}
          </div>
          <div className="metadoc-page-thumbnails__checkbox-wrapper">
            <input
              type="checkbox"
              className="metadoc-page-thumbnails__checkbox"
              checked={isSelected}
              onChange={(e) => onCheckboxChange(pageNum, e)}
              disabled={isInteractionDisabled || selectionLocked}
              aria-label={ARIA_LABELS.selectPage(pageNum)}
              title={
                isDeleted
                  ? ARIA_LABELS.cannotSelectDeleted
                  : selectionLocked
                    ? "Selection disabled for cloned pages"
                    : disabled
                      ? ARIA_LABELS.cannotSelectDisabled
                      : undefined
              }
            />
          </div>
        </div>
      </div>
    );
  },
);

MetaDocThumbnailItem.displayName = "MetaDocThumbnailItem";

export default MetaDocThumbnailItem;
